import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const ArchiveContainer = styled.div`
  padding: 2rem 0;
`;

const ArchiveHeader = styled.div`
  margin-bottom: 2rem;
  text-align: center;
`;

const ArchiveTitle = styled.h1`
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
`;

const ArchiveDescription = styled.p`
  font-size: 1.1rem;
  max-width: 700px;
  margin: 0 auto;
`;

const FilterBar = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const FilterButton = styled.button`
  background: ${props => props.active ? props.theme.colors.primary : 'white'};
  color: ${props => props.active ? 'white' : props.theme.colors.text};
  border: 1px solid ${props => props.theme.colors.divider};
  border-radius: 4px;
  padding: 0.5rem 1rem;
  font-family: ${props => props.theme.fonts.accent};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.active ? props.theme.colors.primary : '#f8f8f8'};
  }
`;

const ArchiveGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const IssueCard = styled.div`
  background-color: white;
  border: 1px solid ${props => props.theme.colors.divider};
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    filter: sepia(0.8) hue-rotate(15deg) saturate(1.2);
  }
`;

const IssueThumbnail = styled.div`
  width: 100%;
  height: 300px;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  border-bottom: 1px solid ${props => props.theme.colors.divider};
`;

const IssueInfo = styled.div`
  padding: 1rem;
  text-align: center;
`;

const IssueDate = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
`;

const IssueNumber = styled.p`
  font-family: ${props => props.theme.fonts.accent};
  font-size: 0.9rem;
  color: ${props => props.theme.colors.text};
  opacity: 0.8;
`;

const LoadMoreButton = styled.button`
  background-color: white;
  border: 2px solid ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.primary};
  font-family: ${props => props.theme.fonts.accent};
  font-weight: 600;
  padding: 0.75rem 2rem;
  border-radius: 4px;
  margin: 3rem auto 0;
  display: block;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    color: white;
  }
`;

const ArchivePage = () => {
  const [issues, setIssues] = useState([]);
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [visibleCount, setVisibleCount] = useState(8);
  
  useEffect(() => {
    // In a real implementation, you would fetch issues from Firestore
    // For demo purposes, create placeholder issues
    const placeholderIssues = [];
    
    // Generate issues for the past 2 years (24 months)
    const currentDate = new Date();
    for (let i = 0; i < 24; i++) {
      const issueDate = new Date(currentDate);
      issueDate.setMonth(currentDate.getMonth() - i);
      
      const year = issueDate.getFullYear();
      const month = issueDate.toLocaleString('default', { month: 'long' });
      
      placeholderIssues.push({
        id: `issue-${i}`,
        date: issueDate,
        displayDate: `${month} ${year}`,
        issueNumber: `Vol. ${Math.floor(i / 12) + 1}, Issue ${(i % 12) + 1}`,
        thumbnailUrl: `https://source.unsplash.com/random/800x1000/?newspaper,${i}`,
        year: year
      });
    }
    
    setIssues(placeholderIssues);
    setFilteredIssues(placeholderIssues);
  }, []);
  
  const filterIssues = (filter) => {
    setActiveFilter(filter);
    
    if (filter === 'all') {
      setFilteredIssues(issues);
    } else {
      const filtered = issues.filter(issue => issue.year.toString() === filter);
      setFilteredIssues(filtered);
    }
    
    setVisibleCount(8);
  };
  
  const loadMore = () => {
    setVisibleCount(prev => prev + 8);
  };
  
  // Get unique years for filter buttons
  const years = [...new Set(issues.map(issue => issue.year))].sort((a, b) => b - a);
  
  return (
    <ArchiveContainer>
      <div className="container">
        <ArchiveHeader>
          <ArchiveTitle>Archive</ArchiveTitle>
          <ArchiveDescription>
            Browse through past editions of the Spartan News. Each issue captures a moment in Schurr High's history.
          </ArchiveDescription>
        </ArchiveHeader>
        
        <FilterBar>
          <FilterButton 
            active={activeFilter === 'all'}
            onClick={() => filterIssues('all')}
          >
            All Issues
          </FilterButton>
          
          {years.map(year => (
            <FilterButton 
              key={year}
              active={activeFilter === year.toString()}
              onClick={() => filterIssues(year.toString())}
            >
              {year}
            </FilterButton>
          ))}
        </FilterBar>
        
        <ArchiveGrid>
          {filteredIssues.slice(0, visibleCount).map(issue => (
            <Link to={`/archive/${issue.id}`} key={issue.id}>
              <IssueCard>
                <IssueThumbnail src={issue.thumbnailUrl} />
                <IssueInfo>
                  <IssueDate>{issue.displayDate}</IssueDate>
                  <IssueNumber>{issue.issueNumber}</IssueNumber>
                </IssueInfo>
              </IssueCard>
            </Link>
          ))}
        </ArchiveGrid>
        
        {visibleCount < filteredIssues.length && (
          <LoadMoreButton onClick={loadMore}>
            Load More Issues
          </LoadMoreButton>
        )}
      </div>
    </ArchiveContainer>
  );
};

export default ArchivePage;